import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Box, CircularProgress, Stack, } from '@mui/material';
import { ArrowCircleDownTwoTone as ArrowCircleDownIcon } from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { replaceStylesheetGlobals } from '../portal/theme/portalTheme';
import { useConversationStore } from '../ConversationStoreProvider';
import { useSelectionWizard } from './wizard/SelectionWizardContext';
// import { MediaCardConfig } from 'portal/item/card/VerticalMediaCard';
import Message from './Message';
import buildSx from './buildSx';
import DefaultProcessingIcon from './DefaultProcessingIcon';
import Image from './Image';
import ProductSpecificPrompts from './ProductSpecificPrompts';
// import referencesToBatchKeys from './referencesToBatchKeys';
/** Min clientHeight for layout-stable scroll check; avoids wrong state during initial flex. */
const MIN_CLIENT_HEIGHT_FOR_SCROLL_CHECK = 50;
/**
 * References to Item Mapping Notes
 *
 * The completion optionally includes a list of references that were used
 * to provide the user with the information.
 *
 * References are typically parts of documents, courses, products, meetings, etc.
 * The reference is typically to a small part of the item, such as a page in a document or
 * a lesson in a course.
 *
 * The same reference might be cited multiple times in the completion. Multiple referecences
 * might be to a single item - such as different lessons in a course.
 *
 * We only want to show items once, and then if the user selects the citation note, highlight the
 * item in the list.
 *
 * To support this we need to:
 *  - Get the references from the completion
 *  - Load the items from the references
 *  - Display the items
 *  - Update the reference with the item or item index so that it can be highlighted
 */
const StreamingMessages = ({ messageConfig, mediaCardConfig, isLoading, saveFeedback, isMobileView, animatedProcessingImage, productPrompts, productPromptHeader, onProductPromptClick, showProductPrompts, onProductClick, hideRightRailOnMobile, maxItemsInRightRail, productFormatting, topSpaceReserved = '0px', disableAutoScroll = false, streamingMessagesScrollRef, hideFirstMessage = false, disableInternalScroll = false, hideScrollDownIcon = false, disableAddSpaceAnimation = false, messageStackMode, chatMessagesLength = 0, }) => {
    const store = useConversationStore();
    const conversation = store.currentConversation;
    const [showMoreButton, setShowMoreButton] = useState(false);
    const showMoreButtonRef = useRef(false);
    const [messageHeight, setMessageHeight] = useState(0);
    const [skeletonHeight, setSkeletonHeight] = useState(0);
    const lastMessageRef = useRef(null);
    const scrollRef = useRef(null);
    const [scrollContainerReady, setScrollContainerReady] = useState(false);
    const containerRef = useRef(null);
    const gifBoxRef = useRef(null);
    // const messagesRef = useRef<any[]>([]);
    const anchor = useRef(null);
    const { stylesheet } = store;
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    const isLastAssistantMessage = lastMessage && lastMessage.role === 'assistant';
    const { pendingInlineWizard, pendingComposerEdit } = useSelectionWizard();
    const lastAssistantPendingWizardTools = isLastAssistantMessage
        ? (lastMessage?.toolInvocations?.filter((t) => t.toolName === 'selectionWizard' && t.result == null) ?? [])
        : [];
    const lastMessageHasPendingWizard = Boolean(pendingComposerEdit
        || (lastAssistantPendingWizardTools.length > 0
            && (pendingInlineWizard != null || lastAssistantPendingWizardTools.length === 1)));
    // Optimized scroll offset height parser with caching and reusable DOM element
    const parseScrollOffsetHeight = useMemo(() => {
        const cache = new Map();
        let tempElement = null;
        const parser = (heightStr) => {
            if (!heightStr || heightStr === '0px')
                return 0;
            // Check cache first
            if (cache.has(heightStr)) {
                return cache.get(heightStr);
            }
            let result = 0;
            // Handle CSS variables like "var(--header-height, 88px)"
            if (heightStr.startsWith('var(')) {
                // Create reusable temporary element if it doesn't exist
                if (!tempElement) {
                    tempElement = document.createElement('div');
                    tempElement.style.position = 'absolute';
                    tempElement.style.visibility = 'hidden';
                    tempElement.style.pointerEvents = 'none';
                    tempElement.style.top = '-9999px';
                    tempElement.style.left = '-9999px';
                    document.body.appendChild(tempElement);
                }
                // Set height and get computed value
                tempElement.style.height = heightStr;
                const computedHeight = window.getComputedStyle(tempElement).height;
                // Parse the computed value (should be in px)
                const match = computedHeight.match(/^([\d.]+)px$/);
                result = match ? parseFloat(match[1]) : 0;
            }
            else {
                // Handle direct values (px, vh, rem, em)
                const match = heightStr.match(/^([\d.]+)(px|vh|rem|em)$/);
                if (match) {
                    const [, value, unit] = match;
                    const numValue = parseFloat(value);
                    switch (unit) {
                        case 'px':
                            result = numValue;
                            break;
                        case 'vh':
                            result = (numValue / 100) * window.innerHeight;
                            break;
                        case 'rem':
                        case 'em':
                            result = numValue * 16;
                            break;
                        default:
                            result = 0;
                    }
                }
            }
            // Cache the result
            cache.set(heightStr, result);
            return result;
        };
        // Add cleanup function to parser
        parser.cleanup = () => {
            if (tempElement && tempElement.parentNode) {
                tempElement.parentNode.removeChild(tempElement);
                tempElement = null;
            }
            cache.clear();
        };
        return parser;
    }, []);
    // Cleanup on unmount
    useEffect(() => () => {
        if (parseScrollOffsetHeight.cleanup) {
            parseScrollOffsetHeight.cleanup();
        }
    }, [parseScrollOffsetHeight]);
    const getAvailableHeight = useCallback(() => {
        if (!scrollRef.current || !anchor.current) {
            return 0;
        }
        const containerHeight = scrollRef.current.clientHeight;
        const scrollOffsetHeightPx = parseScrollOffsetHeight(topSpaceReserved);
        const effectiveContainerHeight = containerHeight - scrollOffsetHeightPx;
        const anchorHeight = anchor.current.clientHeight;
        const userMessageHeight = 120;
        const gifBoxHeight = gifBoxRef.current?.clientHeight || animatedProcessingImage
            ? 160 : 0;
        const maxSkeletonHeight = effectiveContainerHeight
            - anchorHeight - userMessageHeight - gifBoxHeight;
        return Math.max(0, maxSkeletonHeight);
    }, [topSpaceReserved, animatedProcessingImage, parseScrollOffsetHeight]);
    const scrollAnchorWithinContainer = useCallback((behavior = 'smooth') => {
        const container = scrollRef.current;
        const anchorElement = anchor.current;
        if (!container || !anchorElement)
            return;
        const containerRect = container.getBoundingClientRect();
        const anchorRect = anchorElement.getBoundingClientRect();
        const nextTop = container.scrollTop + (anchorRect.top - containerRect.top);
        container.scrollTo({
            top: Math.max(0, nextTop),
            behavior,
        });
    }, []);
    // Reset skeleton height when animation is disabled or loading stops to avoid lingering gaps
    useEffect(() => {
        if (!isLoading || disableAddSpaceAnimation) {
            setSkeletonHeight(0);
        }
    }, [isLoading, disableAddSpaceAnimation]);
    // Keep scroll at top when auto-scroll is disabled (only if internal scroll is disabled)
    // or auto-scroll to anchor in normal mode
    useEffect(() => {
        if (disableAutoScroll) {
            if (disableInternalScroll && scrollRef.current) {
                // In dual mode with internal scroll disabled, keep scroll at top
                // This ensures new messages don't cause scroll, content stays capped
                scrollRef.current.scrollTop = 0;
            }
            return;
        }
        // Normal mode: auto-scroll to anchor when new user message arrives
        if (conversation.messages.length === 0)
            return;
        if (lastMessage && lastMessage.role === 'user' && anchor.current) {
            setTimeout(() => {
                scrollAnchorWithinContainer('smooth');
            }, 10);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        conversation.messages.length,
        isLoading,
        disableAutoScroll,
        disableInternalScroll,
        scrollAnchorWithinContainer,
    ]);
    useLayoutEffect(() => {
        if (scrollRef.current && anchor.current) {
            const height = getAvailableHeight();
            setSkeletonHeight(height);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation.messages.length, isLoading]);
    useEffect(() => {
        // Only attach onLoad handler if an asset is set
        if (isLoading && animatedProcessingImage && gifBoxRef.current) {
            const img = gifBoxRef.current.querySelector('img');
            if (img) {
                const onLoadHandler = () => {
                    setTimeout(() => {
                        if (scrollRef.current && anchor.current) {
                            const newHeight = getAvailableHeight();
                            setSkeletonHeight(newHeight);
                        }
                    }, 50);
                };
                img.addEventListener('load', onLoadHandler);
            }
        }
    }, [
        isLoading,
        animatedProcessingImage,
        getAvailableHeight,
    ]);
    const additionalQuestionProps = useMemo(() => {
        switch (messageConfig.questionAlignment) {
            case 'left':
                return { width: '100%', position: 'relative', marginLeft: '0' };
            case 'right':
                return { width: '100%', position: 'relative', textAlign: 'right' };
            case 'right-60':
                return {
                    width: '60%',
                    position: 'relative',
                    marginLeft: 'auto',
                    textAlign: 'left',
                };
            default:
                return {};
        }
    }, [messageConfig.questionAlignment]);
    const questionStyle = useMemo(() => buildSx(messageConfig.questionStyle, stylesheet, additionalQuestionProps), [messageConfig.questionStyle, stylesheet, additionalQuestionProps]);
    const additionalResponseProps = useMemo(() => {
        switch (messageConfig.responseAlignment) {
            case 'left':
                return { width: '100%', position: 'relative' };
            case 'leftIndent':
                return { width: '100%', position: 'relative', marginLeft: '20px' };
            case 'right':
                return {
                    width: '100%',
                    position: 'relative',
                    marginLeft: 'auto',
                    textAlign: 'right',
                };
            default:
                return {};
        }
    }, [messageConfig.responseAlignment]);
    const responseStyle = useMemo(() => buildSx(messageConfig.responseStyle, stylesheet, additionalResponseProps), [messageConfig.responseStyle, stylesheet, additionalResponseProps]);
    // Find the element that actually scrolls (self or scrollable ancestor when overflow:hidden)
    const getEffectiveScrollContainer = useCallback(() => {
        const el = scrollRef.current;
        if (!el)
            return null;
        const selfStyle = window.getComputedStyle(el);
        const selfScrolls = (selfStyle.overflowY === 'auto' || selfStyle.overflowY === 'scroll'
            || selfStyle.overflow === 'auto' || selfStyle.overflow === 'scroll')
            && el.scrollHeight > el.clientHeight;
        if (selfScrolls)
            return el;
        let parent = el.parentElement;
        while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent);
            const canScroll = (style.overflowY === 'auto' || style.overflowY === 'scroll'
                || style.overflow === 'auto' || style.overflow === 'scroll')
                && parent.scrollHeight > parent.clientHeight;
            if (canScroll)
                return parent;
            parent = parent.parentElement;
        }
        return el;
    }, []);
    const updateArrow = useCallback(() => {
        const scrollEl = getEffectiveScrollContainer();
        if (!scrollEl || hideScrollDownIcon)
            return;
        const { scrollTop, scrollHeight, clientHeight } = scrollEl;
        const layoutStable = clientHeight >= MIN_CLIENT_HEIGHT_FOR_SCROLL_CHECK;
        const isScrollable = layoutStable && scrollHeight > clientHeight;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
        const shouldShow = layoutStable && isScrollable && !isAtBottom;
        if (showMoreButtonRef.current !== shouldShow) {
            showMoreButtonRef.current = shouldShow;
            setShowMoreButton(shouldShow);
        }
    }, [getEffectiveScrollContainer, hideScrollDownIcon]);
    useEffect(() => {
        showMoreButtonRef.current = showMoreButton;
    }, [showMoreButton]);
    const lastMessageContentLength = lastMessage?.content?.length ?? 0;
    // Primary trigger: content changes (streaming chunks, new messages, stream end).
    // useLayoutEffect runs after DOM commit, before paint — layout values are correct.
    useLayoutEffect(() => {
        updateArrow();
    }, [
        conversation.messages.length,
        lastMessageContentLength,
        isLoading,
        updateArrow,
    ]);
    // When internal scroll is enabled, auto-scroll to bottom ONCE when a user message is created
    useEffect(() => {
        if (disableInternalScroll) {
            return;
        }
        if (!conversation.messages.length) {
            return;
        }
        if (!lastMessage || lastMessage.role !== 'user') {
            return;
        }
        if (!scrollRef.current) {
            return;
        }
        setTimeout(() => {
            scrollRef.current?.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
            });
            setTimeout(() => {
                updateArrow();
            }, 200);
        }, 10);
    }, [
        conversation.messages.length,
        lastMessage,
        disableInternalScroll,
        updateArrow,
    ]);
    // Secondary trigger: user scroll. No observers, no throttle — just scroll listener.
    useEffect(() => {
        if (!scrollContainerReady)
            return undefined;
        const effectiveScroll = getEffectiveScrollContainer();
        if (!effectiveScroll)
            return undefined;
        effectiveScroll.addEventListener('scroll', updateArrow, { passive: true });
        return () => {
            effectiveScroll.removeEventListener('scroll', updateArrow);
        };
    }, [scrollContainerReady, updateArrow, getEffectiveScrollContainer]);
    useEffect(() => {
        if (disableAutoScroll) {
            const scrollEl = getEffectiveScrollContainer();
            if (scrollEl) {
                scrollEl.scrollTop = 0;
            }
            updateArrow();
            return;
        }
        if (conversation.messages.length > 0 && scrollRef.current) {
            setTimeout(() => {
                scrollRef.current?.scrollTo({
                    top: scrollRef.current.scrollHeight,
                    behavior: 'smooth',
                });
                // Check scroll position after scroll animation completes
                setTimeout(() => {
                    updateArrow();
                }, 500);
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        disableAutoScroll,
        updateArrow,
        getEffectiveScrollContainer,
    ]);
    useLayoutEffect(() => {
        if (lastMessageRef.current) {
            const height = Math.min(100, lastMessageRef.current.clientHeight);
            setMessageHeight(height);
        }
    }, [conversation.messages.length, isLoading]);
    const scrollDown = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy(0, scrollRef.current.clientHeight * 0.8);
            // Check scroll position after scroll completes
            setTimeout(() => {
                updateArrow();
            }, 100);
        }
    };
    const hasCarouselItems = conversation.messages.some((m) => m.carouselItems && Object.values(m.carouselItems).length > 0);
    // console.log('messages', conversation.messages);
    // Calculate lastUserMessageIndex using original messages array
    const lastUserMessageIndex = conversation.messages.reduce((lastIndex, message, index) => (message.role !== 'assistant' ? index : lastIndex), -1);
    // Adjust lastUserMessageIndex if first message is hidden
    const adjustedLastUserMessageIndex = hideFirstMessage && lastUserMessageIndex > 0
        ? lastUserMessageIndex - 1
        : lastUserMessageIndex;
    // Filter messages to render (hide first message if hideFirstMessage is true)
    // Always define messagesToRender, even if empty, to ensure hooks order consistency
    let messagesToRender = [];
    if (conversation.hasMessages()) {
        if (hideFirstMessage && conversation.messages.length > 0) {
            messagesToRender = conversation.messages.slice(1);
        }
        else {
            messagesToRender = conversation.messages;
        }
    }
    let flexValue = 1;
    if (messageStackMode === 'natural' || disableInternalScroll) {
        flexValue = '0 1 auto';
    }
    let minHeightValue = 0;
    if (messageStackMode === 'natural' || disableInternalScroll) {
        minHeightValue = 'auto';
    }
    const awaitingMobxSync = !conversation.hasMessages() && chatMessagesLength > 0;
    if (awaitingMobxSync) {
        return (_jsx(Box, { sx: {
                flex: flexValue,
                flexShrink: messageStackMode === 'natural' || disableInternalScroll ? undefined : 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                width: '100%',
                minHeight: messageStackMode === 'natural' || disableInternalScroll ? 120 : minHeightValue,
                py: 4,
            }, children: _jsx(CircularProgress, { size: 28 }) }));
    }
    return (conversation.hasMessages() ? (
    // <Collapse
    //   in={conversation.hasMessages()}
    //   timeout={1000}
    //   unmountOnExit
    //   sx={{ flexGrow: 1, overflow: 'hidden' }}
    // >
    _jsxs(Box, { ref: containerRef, sx: {
            flex: flexValue,
            flexShrink: messageStackMode === 'natural' || disableInternalScroll ? undefined : 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            width: '100%',
            minHeight: minHeightValue,
        }, children: [_jsxs(Stack, { "data-mcn-streaming-scroll": "1", sx: {
                    flex: disableInternalScroll ? '0 1 auto' : 1,
                    overflow: disableInternalScroll ? 'hidden' : 'auto',
                    padding: 1,
                }, ref: (el) => {
                    // Assign to internal scrollRef
                    scrollRef.current = el;
                    // Also assign to external ref if provided (for dual mode See More button detection)
                    if (streamingMessagesScrollRef) {
                        streamingMessagesScrollRef.current = el;
                    }
                    // Trigger effect re-run when scroll container mounts/unmounts
                    setScrollContainerReady(!!el);
                }, children: [messagesToRender.map((message, index) => {
                        // Calculate original index in the full messages array
                        const originalIndex = hideFirstMessage ? index + 1 : index;
                        const isLastMessageOverall = originalIndex === conversation.messages.length - 1;
                        let computedMessageStyle;
                        if (message.role === 'assistant') {
                            if (isLastMessageOverall) {
                                computedMessageStyle = { ...responseStyle, minHeight: `${messageHeight}px` };
                            }
                            else {
                                computedMessageStyle = responseStyle;
                            }
                        }
                        else {
                            computedMessageStyle = questionStyle;
                        }
                        // Separate consecutive messages so a reply doesn't visually merge into the
                        // next one (MCN-2387, most visible on mobile). Skip the last message to avoid
                        // a trailing gap before the processing icon / scroll padding.
                        const isLastRendered = index === messagesToRender.length - 1;
                        return (_jsxs(React.Fragment, { children: [index === adjustedLastUserMessageIndex && _jsx("div", { id: "anchor", ref: anchor }), _jsx(Box, { sx: { mb: isLastRendered ? 0 : { xs: 2, sm: 1.5 } }, children: _jsx(Message, { message: message, conversation: conversation, messageConfig: {
                                            ...messageConfig,
                                            showFeedbackButtons: Boolean(messageConfig.showFeedbackButtons)
                                                && message.role === 'assistant'
                                                && isLastMessageOverall
                                                && !isLoading,
                                            hideRightRailOnMobile: hideRightRailOnMobile ?? false,
                                            maxItemsInRightRail: maxItemsInRightRail ?? 0,
                                        }, mediaCardConfig: mediaCardConfig, messageStyle: computedMessageStyle, saveFeedback: saveFeedback, isMobileView: isMobileView, hasCarouselItems: hasCarouselItems, showProcessingIcon: isLoading && isLastMessageOverall && isLastAssistantMessage && !lastMessageHasPendingWizard, processingIconImage: animatedProcessingImage, onProductClick: onProductClick, productFormatting: productFormatting }) })] }, message.id));
                    }), isLoading && !isLastAssistantMessage && (_jsx(Box, { ref: gifBoxRef, sx: {
                            textAlign: 'left',
                            mt: 2,
                            width: '100%',
                            minHeight: '160px',
                        }, children: animatedProcessingImage ? (_jsx(Image, { url: animatedProcessingImage.url, status: animatedProcessingImage.status, alt: animatedProcessingImage.alt, className: animatedProcessingImage.className, avatar: animatedProcessingImage.avatar, avatarVariant: animatedProcessingImage.avatarVariant, customStyle: {
                                objectFit: 'contain',
                            } })) : (_jsx(DefaultProcessingIcon, { color: replaceStylesheetGlobals(stylesheet, messageConfig?.defaultProcessingIcon?.color || 'black'), width: messageConfig?.defaultProcessingIcon?.width, height: messageConfig?.defaultProcessingIcon?.height })) })), showProductPrompts
                        && productPrompts
                        && productPrompts.length > 0
                        && onProductPromptClick && (_jsx(ProductSpecificPrompts, { prompts: productPrompts, headerMessage: productPromptHeader, onPromptClick: onProductPromptClick, isLoading: isLoading })), isLoading && !disableAddSpaceAnimation && !disableInternalScroll && (_jsx(Box, { sx: { minHeight: `${skeletonHeight}px` } }))] }), !hideScrollDownIcon && (_jsx(ArrowCircleDownIcon, { fontSize: "large", onClick: scrollDown, sx: {
                    display: showMoreButton ? 'block' : 'none',
                    position: 'absolute',
                    bottom: 10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    cursor: 'pointer',
                    color: replaceStylesheetGlobals(stylesheet, 'color_primary') || '#3b82f6',
                    backgroundColor: replaceStylesheetGlobals(stylesheet, 'color_background') || '#ffffff',
                    borderRadius: '999px',
                    padding: '2px',
                    boxShadow: '0 6px 14px rgba(0, 0, 0, 0.2)',
                } }))] })
    // </Collapse>
    ) : null);
};
StreamingMessages.defaultProps = {
    animatedProcessingImage: undefined,
    productPrompts: undefined,
    productPromptHeader: undefined,
    onProductPromptClick: (() => { }),
    showProductPrompts: false,
    onProductClick: undefined,
    hideRightRailOnMobile: undefined,
    maxItemsInRightRail: undefined,
    productFormatting: undefined,
    topSpaceReserved: '0px',
    disableAutoScroll: false,
    streamingMessagesScrollRef: undefined,
    hideFirstMessage: false,
    disableInternalScroll: false,
    hideScrollDownIcon: false,
    disableAddSpaceAnimation: false,
    chatMessagesLength: 0,
    // messageStackMode should come from parent, not use default
    messageStackMode: undefined,
};
export default observer(StreamingMessages);
//# sourceMappingURL=StreamingMessages.js.map